import type { FC } from 'react'

import { useEffect, useRef } from 'react'
import { sceneInput, uiStore } from '@/lib/scene-state'

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

type Chapter = {
    element: HTMLElement
    top: number
    height: number
    hold: number
}

/**
 * Maps the page scroll onto the 3D story. Each `[data-chapter]` section owns one shot: the
 * shot is reached when the section is centered in the viewport. Also writes `--fade` and
 * `--drift` on every chapter so its copy fades and floats with the scroll.
 */
export const SceneTracker: FC = () => {
    const bar = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
        const applyMotion = () => {
            sceneInput.reducedMotion = motion.matches
        }

        applyMotion()
        motion.addEventListener('change', applyMotion)

        let chapters: Chapter[] = []
        let anchors: number[] = []
        let frameId = 0

        const measure = () => {
            const viewport = window.innerHeight
            const scroll = window.scrollY

            chapters = Array.from(document.querySelectorAll<HTMLElement>('[data-chapter]'))
                .sort((a, b) => Number(a.dataset.chapter) - Number(b.dataset.chapter))
                .map(element => {
                    const rect = element.getBoundingClientRect()

                    return {
                        element,
                        top: rect.top + scroll,
                        height: rect.height,
                        hold: Number(element.dataset.hold ?? 0.12)
                    }
                })

            anchors = chapters.map(chapter => Math.max(0, chapter.top + chapter.height / 2 - viewport / 2))

            for (let index = 1; index < anchors.length; index++) {
                anchors[index] = Math.max(anchors[index], anchors[index - 1] + 1)
            }
        }

        const update = () => {
            frameId = 0

            const y = window.scrollY
            const viewport = window.innerHeight
            const count = anchors.length
            let chapter = 0

            if (count > 1 && y > anchors[0]) {
                if (y >= anchors[count - 1]) chapter = count - 1
                else {
                    for (let index = 0; index < count - 1; index++) {
                        if (y < anchors[index + 1]) {
                            chapter = index + (y - anchors[index]) / (anchors[index + 1] - anchors[index])
                            break
                        }
                    }
                }
            }

            sceneInput.chapter = chapter

            const active = Math.round(chapter)

            if (uiStore.get().activeChapter !== active) uiStore.set({ activeChapter: active })

            chapters.forEach(({ element, top, height, hold }) => {
                const offset = (top + height / 2 - y - viewport / 2) / viewport
                const fade = clamp(1 - (Math.abs(offset) - hold) / 0.36, 0, 1)

                element.style.setProperty('--fade', fade.toFixed(3))
                element.style.setProperty('--drift', (-offset * 90).toFixed(1))
            })

            if (bar.current) {
                const max = document.documentElement.scrollHeight - viewport

                bar.current.style.transform = `scaleX(${max > 0 ? clamp(y / max, 0, 1) : 0})`
            }
        }

        const schedule = () => {
            if (!frameId) frameId = requestAnimationFrame(update)
        }

        const refresh = () => {
            measure()
            schedule()
        }

        const onPointer = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse') return

            sceneInput.pointerX = (event.clientX / window.innerWidth) * 2 - 1
            sceneInput.pointerY = (event.clientY / window.innerHeight) * 2 - 1
        }

        const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(refresh) : null

        measure()
        update()

        resizeObserver?.observe(document.body)
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', refresh)
        window.addEventListener('load', refresh)
        window.addEventListener('pointermove', onPointer, { passive: true })

        return () => {
            motion.removeEventListener('change', applyMotion)
            resizeObserver?.disconnect()
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', refresh)
            window.removeEventListener('load', refresh)
            window.removeEventListener('pointermove', onPointer)
            cancelAnimationFrame(frameId)
        }
    }, [])

    return (
        <div
            ref={bar}
            aria-hidden='true'
            className='pointer-events-none fixed inset-x-0 top-0 z-[60] h-0.5 origin-left bg-brand'
            style={{ transform: 'scaleX(0)' }}
        />
    )
}
